/**
 * Google Automated Discounts - pv2 JWT handler
 * Reads the pv2 URL param from Google Shopping ads, validates via CF Worker,
 * displays the discounted price, and applies the discount code at checkout.
 *
 * Supports all product sections:
 *   starmap-v2                             -> #sm2-price
 *   moon-skylit, simple-product            -> .add__cart--price p / #sticky-price
 *   hor-moon-skylit                        -> .h-add__cart--price p
 *   locationmap                            -> #lm-price
 *   custom-print                           -> #price-display
 */
(function () {
  'use strict';

  var WORKER_URL = 'https://skylit-google-discount.skylitstudio-ltd.workers.dev';
  var STORAGE_KEY = 'google_auto_discount';
  var PAGE_TTL = 30 * 60 * 1000;
  var CART_TTL = 48 * 60 * 60 * 1000;
  var _applyTimeout = null;

  // localStorage survives new tabs/windows; Google requires the discounted
  // price to persist for 30 min even when the shopper revisits without the
  // JWT in the URL, which a fresh tab + sessionStorage would fail. Fall back
  // to an in-memory map when storage is unavailable (private mode quota).
  var _mem = {};
  function storeGet(k) {
    try { return localStorage.getItem(k) || sessionStorage.getItem(k) || _mem[k] || null; }
    catch (e) { return _mem[k] || null; }
  }
  function storeSet(k, v) {
    _mem[k] = v;
    try { localStorage.setItem(k, v); } catch (e) {}
    try { sessionStorage.setItem(k, v); } catch (e) {}
  }
  function storeRemove(k) {
    delete _mem[k];
    try { localStorage.removeItem(k); } catch (e) {}
    try { sessionStorage.removeItem(k); } catch (e) {}
  }

  // All possible price element selectors across product sections
  var PRICE_SELECTORS = [
    '#sm2-price',
    '.add__cart--price p',
    '.h-add__cart--price p',
    '#lm-price',
    '#price-display',
    '#sticky-price',
    '.cust-footer-price-text',  // moon map (moon-skylit)
    '#nl-price-display'         // YOR (name-landsat) — always-visible price above the CTA
  ];

  function findPriceEl() {
    for (var i = 0; i < PRICE_SELECTORS.length; i++) {
      var el = document.querySelector(PRICE_SELECTORS[i]);
      if (el) return el;
    }
    return null;
  }

  function findAllPriceEls() {
    var els = [];
    for (var i = 0; i < PRICE_SELECTORS.length; i++) {
      var el = document.querySelector(PRICE_SELECTORS[i]);
      if (el) els.push(el);
    }
    return els;
  }

  function init() {
    var params = new URLSearchParams(window.location.search);
    var pv2 = params.get('pv2');
    if (pv2) {
      // Always start fresh - clear any previous discount state to prevent
      // double-discounting on repeat visits
      storeRemove(STORAGE_KEY);
      storeRemove('google_discount_code');
      storeRemove('google_discount_expiry');
      // Clear any cached original prices from previous runs
      var els = findAllPriceEls();
      for (var i = 0; i < els.length; i++) delete els[i].dataset.gadOriginal;
      var oldBanner = document.getElementById('gad-discount-banner');
      if (oldBanner) oldBanner.remove();
      validateAndApply(pv2);
      startWatchdog();
    } else {
      restoreFromSession();
      startWatchdog();
    }
  }

  // Late-init watchdog. Customizer boot routines keep rewriting prices and
  // re-selecting variants for many seconds after load (the moon customizer
  // re-asserts the URL variant up to ~12s in), racing the one-shot apply and
  // the MutationObserver's 600ms suppression window — the discount can end
  // up applied to nothing. Poll for the first 30s and re-apply whenever our
  // markup or banner is missing while a valid discount is stored.
  function startWatchdog() {
    var ticks = 0;
    var t = setInterval(function () {
      if (++ticks > 20) return clearInterval(t);
      var stored = storeGet(STORAGE_KEY);
      if (!stored) return clearInterval(t);
      var info;
      try { info = JSON.parse(stored); } catch (e) { return clearInterval(t); }
      if (info.expires < Date.now()) return clearInterval(t);
      var needsApply = !document.getElementById('gad-discount-banner');
      if (!needsApply) {
        var els = findAllPriceEls();
        for (var i = 0; i < els.length; i++) {
          if (els[i].textContent.trim() && !els[i].querySelector('.gad-wrap')) { needsApply = true; break; }
        }
      }
      if (needsApply) applyDiscount(info);
    }, 1500);
  }

  function decodeJWTPayload(token) {
    try {
      var parts = token.split('.');
      if (parts.length !== 3) return null;
      var payload = parts[1].replace(/-/g, '+').replace(/_/g, '/');
      while (payload.length % 4) payload += '=';
      return JSON.parse(atob(payload));
    } catch (e) {
      return null;
    }
  }

  function getVariantFromOffer(offerId) {
    if (!offerId) return null;
    var parts = offerId.split('_');
    return parts.length >= 4 ? parts[parts.length - 1] : null;
  }

  function getCurrentVariantId() {
    // starmap-v2 has no cart <form>; it keeps the live variant in #sm2-variant-id
    // and updates it on every size/finish change. Prefer it so the variant guard
    // tracks the actual selection (the URL ?variant never changes in-session).
    var sm2 = document.getElementById('sm2-variant-id');
    if (sm2 && sm2.value) return sm2.value;
    var input = document.querySelector('form[action*="/cart/add"] input[name="id"]');
    if (input) return input.value;
    var params = new URLSearchParams(window.location.search);
    return params.get('variant');
  }

  // Locale-aware money parser. Shopify presentment formats vary by market:
  // GBP "£1,234.56" (comma thousands), EUR "€1.234,56" / "€45,58" (comma
  // decimal). A naive strip-non-digits turns "€45,58" into 4558, so decide
  // the decimal separator by whichever of . / , appears last.
  function parseMoney(text) {
    var s = String(text).replace(/[^\d.,]/g, '');
    if (!s) return NaN;
    var lastDot = s.lastIndexOf('.');
    var lastComma = s.lastIndexOf(',');
    if (lastComma > lastDot) {
      s = s.replace(/\./g, '').replace(/,/g, '.');
    } else {
      s = s.replace(/,/g, '');
    }
    return parseFloat(s);
  }

  function calcDiscountedPrice(priceText, discountPct) {
    var num = parseMoney(priceText);
    if (isNaN(num)) return null;
    return (num * (1 - discountPct / 100)).toFixed(2);
  }

  async function validateAndApply(token) {
    var payload = decodeJWTPayload(token);
    if (!payload) return;
    if (payload.exp && payload.exp * 1000 < Date.now()) return;

    var jwtVariantId = getVariantFromOffer(payload.o);

    // Render IMMEDIATELY from the decoded payload - Google's review crawler
    // may snapshot the page before a worker roundtrip completes, and the
    // payload already carries everything the display needs (p, dp, c, exp).
    // The worker call below only upgrades this with the checkout code.
    var info = {
      jwt_price: payload.p,
      currency: payload.c,
      discount_pct: payload.dp,
      discount_code: null,
      jwt_variant_id: jwtVariantId,
      expires: payload.exp * 1000,
      stored_at: Date.now(),
    };
    storeAndApply(info, true);

    try {
      var resp = await fetch(WORKER_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: token }),
      });
      if (!resp.ok) throw new Error('Worker error');
      var data = await resp.json();
      if (!data.valid) throw new Error('Invalid token');

      storeAndApply({
        jwt_price: data.price,
        currency: data.currency,
        discount_pct: data.discount_pct,
        discount_code: data.discount_code,
        jwt_variant_id: jwtVariantId,
        expires: data.expires * 1000,
        stored_at: info.stored_at,
      }, true);
    } catch (err) {
      // Worker unreachable - the payload-derived info already rendered above.
    }
  }

  function storeAndApply(info, immediate) {
    storeSet(STORAGE_KEY, JSON.stringify(info));
    scheduleApply(info, immediate);
  }

  function scheduleApply(info, immediate) {
    if (_applyTimeout) clearTimeout(_applyTimeout);
    if (immediate) applyDiscount(info);
    // Re-apply after a beat regardless: theme JS that fills the price element
    // slightly later (e.g. YOR populates #nl-price-display) overwrites our
    // markup, and this delayed pass picks up the real base price.
    _applyTimeout = setTimeout(function () { applyDiscount(info); }, 400);
  }

  function restoreFromSession() {
    try {
      var stored = storeGet(STORAGE_KEY);
      if (!stored) return;
      var info = JSON.parse(stored);
      if (Date.now() - info.stored_at > PAGE_TTL) { storeRemove(STORAGE_KEY); return; }
      if (info.expires < Date.now()) { storeRemove(STORAGE_KEY); return; }
      scheduleApply(info, true);
    } catch (e) {
      storeRemove(STORAGE_KEY);
    }
  }

  function applyDiscount(info) {
    renderBanner(info);
    updatePriceDisplay(info);
    interceptAddToCart(info);
  }

  // Pin the banner to the bottom-right of the PREVIEW area, not the viewport:
  // each customizer has an options panel on the right, and a plain right:18px
  // would sit on top of its controls (theme swatches, step-4 add-to-cart).
  // Anchor just left of the panel's left edge, found via a per-customizer
  // element that is visible from step 1. Mobile keeps the centered layout
  // from the stylesheet (inline right would beat the media query, so clear it).
  var PANEL_ANCHORS = ['#sm2-steps-nav', '#lm-steps-nav', '.cust-footer', '#nl-price-display', '#price-display'];
  function positionBanner(banner) {
    if (window.innerWidth <= 768) { banner.style.right = ''; return; }
    var rect = null;
    for (var i = 0; i < PANEL_ANCHORS.length; i++) {
      var el = document.querySelector(PANEL_ANCHORS[i]);
      if (el) {
        var r = el.getBoundingClientRect();
        if (r.width > 0) { rect = r; break; }
      }
    }
    // 84px inset from the panel edge: the star/location previews keep a
    // vertical zoom/lock control strip hugging the panel side — clear it too.
    banner.style.right = rect
      ? Math.max(18, window.innerWidth - rect.left + 84) + 'px'
      : '18px';
  }

  // Always-visible discount banner. The customizer sections hide their price
  // element until the final step, so Google's automated-discounts reviewer
  // (which snapshots the page as it loads) sees no discounted price at all
  // and fails the "discounted price doesn't show on your product page" check.
  // This banner renders on first paint, on every step, straight from the JWT
  // payload (p = discounted variant price, dp = pct) — no dependency on the
  // theme having painted a price yet.
  function renderBanner(info) {
    var banner = document.getElementById('gad-discount-banner');

    // Product pages only (the script loads theme-wide on page_type product,
    // but restoreFromSession could fire on a product page with no customizer
    // price element — no price context, no banner).
    if (findAllPriceEls().length === 0) return;

    // Honesty guard, same as the price-element path: the minted code only
    // covers the JWT variant. Different selection -> hide the banner.
    var currentVariant = getCurrentVariantId();
    if (info.jwt_variant_id && currentVariant &&
        String(currentVariant) !== String(info.jwt_variant_id)) {
      if (banner) banner.remove();
      return;
    }

    var discounted = parseFloat(info.jwt_price);
    var pct = parseFloat(info.discount_pct);
    if (isNaN(discounted) || isNaN(pct) || pct <= 0 || pct >= 100) return;
    var original = (discounted * 100) / (100 - pct);

    if (!banner) {
      banner = document.createElement('div');
      banner.id = 'gad-discount-banner';
      var style = document.createElement('style');
      style.textContent =
        '#gad-discount-banner{position:fixed;right:18px;bottom:18px;' +
        'z-index:9998;display:inline-flex;align-items:center;gap:9px;padding:9px 16px;' +
        'background:#fff;border:1px solid rgba(195,156,113,.45);border-radius:999px;' +
        'box-shadow:0 6px 20px rgba(0,0,0,.14);font-family:"Josefin Sans",sans-serif;' +
        'font-size:14px;line-height:1;white-space:nowrap;color:#2b2b2b;}' +
        '@media (max-width:768px){#gad-discount-banner{right:auto;left:50%;transform:translateX(-50%);' +
        'bottom:84px;font-size:13px;padding:8px 13px;}}';
      banner.appendChild(style);
      document.body.appendChild(banner);
      window.addEventListener('resize', function () {
        var b = document.getElementById('gad-discount-banner');
        if (b) positionBanner(b);
      });
    }
    positionBanner(banner);
    // Rebuild content (keep the <style> node)
    while (banner.childNodes.length > 1) banner.removeChild(banner.lastChild);
    var html =
      '<span style="background:#d92d20;color:#fff;font-weight:700;font-size:.78em;' +
        'letter-spacing:.04em;padding:4px 8px 3px;border-radius:999px;">' + pct + '% OFF</span>' +
      '<span style="font-weight:700;">' + formatMoney(discounted.toFixed(2), info.currency) + '</span>' +
      '<span style="text-decoration:line-through;color:#9a9a9a;">' +
        formatMoney(original.toFixed(2), info.currency) + '</span>' +
      '<span style="color:#8a8a8a;font-size:.85em;">applied at checkout</span>';
    var wrap = document.createElement('span');
    wrap.style.cssText = 'display:inline-flex;align-items:center;gap:9px;';
    wrap.innerHTML = html;
    banner.appendChild(wrap);

    // Keep the machine-readable signals aligned even while the theme's own
    // price element is still hidden/blank on step 1.
    updateMachineReadablePrice(discounted.toFixed(2), info.currency);
  }

  function formatMoney(amount, currency) {
    if (currency === 'GBP') return '\u00a3' + parseFloat(amount).toFixed(2);
    if (currency === 'EUR') return '\u20ac' + parseFloat(amount).toFixed(2);
    if (currency === 'USD') return '$' + parseFloat(amount).toFixed(2);
    return currency + ' ' + parseFloat(amount).toFixed(2);
  }

  function renderDiscountHTML(originalClean, discountedFormatted, suffix, pct, compact) {
    // Small annotation: red "N% OFF" + struck-through original price.
    var annotation =
      '<span style="display:inline-flex;align-items:center;gap:6px;font-weight:600;letter-spacing:.02em;line-height:1;white-space:nowrap;">' +
        '<span style="color:#d92d20;">' + pct + '% OFF</span>' +
        '<span style="text-decoration:line-through;text-decoration-color:#c4c4c4;color:#9a9a9a;">' + originalClean + suffix + '</span>' +
      '</span>';

    if (compact) {
      // Sticky bar: discounted price then a smaller inline annotation, one line.
      return '<span class="gad-wrap" style="display:inline-flex;align-items:baseline;gap:8px;">' +
               '<span style="font-weight:700;">' + discountedFormatted + suffix + '</span>' +
               '<span style="font-size:0.62em;">' + annotation + '</span>' +
             '</span>';
    }
    // Main price: discounted amount as the headline, with a superscript-style
    // annotation stacked above-right (red % off + strikethrough original).
    return '<span class="gad-wrap" style="display:inline-flex;flex-direction:column;align-items:flex-end;line-height:1.05;">' +
             '<span style="font-size:0.46em;margin-bottom:2px;">' + annotation + '</span>' +
             '<span>' + discountedFormatted + suffix + '</span>' +
           '</span>';
  }

  function updatePriceDisplay(info) {
    var allEls = findAllPriceEls();
    if (allEls.length === 0) return;

    var currentVariant = getCurrentVariantId();

    for (var i = 0; i < allEls.length; i++) {
      var el = allEls[i];
      var container = el.closest('.add__cart--price, .h-add__cart--price') || el.parentElement;

      // Determine the base (pre-discount) price. If our own markup is present
      // (.gad-wrap), trust the cached original; otherwise the element holds the
      // theme's plain price for the current variant — capture it fresh. This is
      // what makes the variant-change race self-heal: every time the theme
      // rewrites the price, the wrap is gone and we re-read the real price.
      var hasOurMarkup = !!el.querySelector('.gad-wrap');
      var originalText;
      if (hasOurMarkup) {
        // Our markup is showing. Its text ("5% OFF£74.98£71.23") is NOT a
        // base price - only ever trust the cached original here, even when
        // that cache is empty (element was blank at first paint).
        originalText = el.dataset.gadOriginal || '';
      } else {
        originalText = el.textContent.trim();
        el.dataset.gadOriginal = originalText;
      }

      // money_with_currency appends the ISO code ("£31.99 GBP", "€45,58 EUR")
      // — split it off whatever the market currency is, and re-append it to
      // both prices so the strikethrough and the discounted price match.
      var suffixMatch = originalText.match(/\s+([A-Z]{3})\s*$/);
      var cleanPrice = suffixMatch
        ? originalText.slice(0, suffixMatch.index).trim()
        : originalText.trim();
      var suffix = suffixMatch ? ' ' + suffixMatch[1] : '';

      // Honesty guard: the Google discount code is entitled ONLY to the
      // authorized (JWT) variant. If the shopper is on a different variant the
      // code won't apply at checkout, so don't advertise a discount we can't
      // honour. Restore the real price only if our markup is currently shown
      // (avoids a mutation->reapply loop when the price is already plain).
      if (info.jwt_variant_id && currentVariant &&
          String(currentVariant) !== String(info.jwt_variant_id)) {
        if (hasOurMarkup && el.dataset.gadOriginal) el.textContent = el.dataset.gadOriginal;
        continue;
      }

      // Calculate discounted price. The Google code discounts ONLY the entitled
      // variant (the print). The displayed price can include undiscounted add-ons
      // (ribbon/gift) as separate line items, so subtract just the variant's
      // discount amount from the shown total — never discount the whole sum, or
      // we'd promise a reduction on the ribbon that checkout won't honour.
      // variant discount = jwt_price * dp / (100 - dp)  (jwt_price is post-discount)
      var discountedAmount;
      var jwtMatch = String(currentVariant) === String(info.jwt_variant_id) && info.jwt_price;
      if (jwtMatch) {
        var shownTotal = parseMoney(cleanPrice);
        var jwtPriceNum = parseFloat(info.jwt_price);
        var variantDiscount = (jwtPriceNum * info.discount_pct) / (100 - info.discount_pct);
        if (!isNaN(shownTotal)) {
          discountedAmount = (shownTotal - variantDiscount).toFixed(2);
        } else {
          // Theme hasn't painted the base price yet (e.g. YOR fills its price
          // element async). Don't wait: show the JWT price now and derive the
          // original from p and dp so the strikethrough isn't blank. The
          // 400ms delayed re-apply corrects against the real base later.
          discountedAmount = jwtPriceNum.toFixed(2);
          cleanPrice = formatMoney((jwtPriceNum + variantDiscount).toFixed(2), info.currency);
          el.dataset.gadOriginal = cleanPrice;
        }
      } else {
        discountedAmount = calcDiscountedPrice(cleanPrice, info.discount_pct);
      }
      if (!discountedAmount || isNaN(parseFloat(discountedAmount))) continue;

      var discountedFormatted = formatMoney(discountedAmount, info.currency);
      if (jwtMatch) updateMachineReadablePrice(discountedAmount, info.currency);

      // Sticky price gets compact one-line layout; main price gets the
      // discounted headline with the superscript annotation stacked above.
      var compact = el.id === 'sticky-price';
      el.innerHTML = renderDiscountHTML(cleanPrice, discountedFormatted, suffix, info.discount_pct, compact);
    }
  }

  // Google's review crawler extracts "the page price" from machine-readable
  // signals as well as visible text. The theme's og:price:amount carries the
  // product's MIN variant price (e.g. 21.99 on a 74.98 variant), which
  // contradicts the discounted price we render and can fail the automated
  // discounts review. While a discount is active, align the meta signals and
  // publish a Product JSON-LD offer with the discounted (sale) price.
  function updateMachineReadablePrice(amount, currency) {
    try {
      var og = document.querySelector('meta[property="og:price:amount"]');
      if (og) og.setAttribute('content', String(amount));
      var ld = document.getElementById('gad-jsonld');
      if (!ld) {
        ld = document.createElement('script');
        ld.type = 'application/ld+json';
        ld.id = 'gad-jsonld';
        document.head.appendChild(ld);
      }
      ld.textContent = JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'Product',
        'name': document.title.split('|')[0].trim(),
        'url': location.origin + location.pathname + location.search.replace(/([?&])pv2=[^&]*&?/, '$1').replace(/[?&]$/, ''),
        'offers': {
          '@type': 'Offer',
          'price': String(amount),
          'priceCurrency': currency,
          'availability': 'https://schema.org/InStock'
        }
      });
    } catch (e) { /* display already succeeded; metadata is best-effort */ }
  }

  function interceptAddToCart(info) {
    if (!info.discount_code) return;
    storeSet('google_discount_code', info.discount_code);
    storeSet('google_discount_expiry', String(Date.now() + CART_TTL));

    var form = document.querySelector('form[action*="/cart/add"]');
    if (!form || form.dataset.googleDiscountHooked) return;
    form.dataset.googleDiscountHooked = 'true';

    var origFetch = window.fetch;
    window.fetch = function (url, opts) {
      var result = origFetch.apply(this, arguments);
      if (typeof url === 'string' && url.includes('/cart/add')) {
        result.then(function () {
          var code = storeGet('google_discount_code');
          var expiry = storeGet('google_discount_expiry');
          if (code && expiry && parseInt(expiry) > Date.now()) {
            applyDiscountCode(code, origFetch);
          }
        }).catch(function () {});
      }
      return result;
    };
  }

  function applyDiscountCode(code, fetchFn) {
    var fn = fetchFn || window.fetch;
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    fn(root + 'cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ discount: code }),
    }).catch(function () {});
  }

  function watchVariantChanges() {
    var _reapplying = false;

    // Watch each price element directly for content changes
    // When theme JS overwrites innerHTML (e.g. variant change), we detect it
    // and re-apply the discount with the new base price
    var allEls = findAllPriceEls();
    for (var i = 0; i < allEls.length; i++) {
      if (allEls[i].dataset.gadWatched) continue;
      allEls[i].dataset.gadWatched = 'true';
      (function (el) {
        new MutationObserver(function () {
          if (_reapplying) return;
          var stored = storeGet(STORAGE_KEY);
          if (!stored) return;
          // Theme overwrote our content - the new text is the new variant's price.
          // Only re-apply if our discount markup (.gad-wrap) was removed; if it's
          // still present this mutation is our own render, so ignore it.
          if (!el.querySelector('.gad-wrap')) {
            // Theme set a new price - update gadOriginal and re-apply
            el.dataset.gadOriginal = el.textContent.trim();
            // Also update the banner
            var banner = document.getElementById('gad-discount-banner');
            if (banner) banner.remove();
            _reapplying = true;
            scheduleApply(JSON.parse(stored));
            setTimeout(function () { _reapplying = false; }, 600);
          }
        }).observe(el, { childList: true, subtree: true, characterData: true });
      })(allEls[i]);
    }

    // Also watch variant input for value attribute changes
    var variantInput = document.querySelector('form[action*="/cart/add"] input[name="id"]');
    if (variantInput && !variantInput.dataset.gadWatched) {
      variantInput.dataset.gadWatched = 'true';
      new MutationObserver(function () {
        if (_reapplying) return;
        var stored = storeGet(STORAGE_KEY);
        if (!stored) return;
        // Clear cached prices for all elements
        var els = findAllPriceEls();
        for (var j = 0; j < els.length; j++) {
          delete els[j].dataset.gadOriginal;
        }
        var banner = document.getElementById('gad-discount-banner');
        if (banner) banner.remove();
        _reapplying = true;
        scheduleApply(JSON.parse(stored));
        setTimeout(function () { _reapplying = false; }, 600);
      }).observe(variantInput, { attributes: true, attributeFilter: ['value'] });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); watchVariantChanges(); });
  } else {
    init();
    watchVariantChanges();
  }
})();
